import { TimeSlotType } from '../bookings';
import { DayOfWeek } from '../nutrition';
import { IPayfastIntegrationConfig, Organisation } from '../payments';
import { ICompany } from '../payments/company';
import { GymTimeZone, timeZoneISO2CountryMap } from './gym-locale';

export enum GymTypes {
  Indoor = 'Indoor',
  Outdoor = 'Outdoor',
  Home = 'Home',
  Elite = 'Elite',
}

export enum GymEnvironment {
  Staging = 'staging',
  Production = 'production',
  None = 'none',
}

export interface IFlattenedTimeType {
  start: string;
  end: string;
  type: TimeSlotType;
}

export interface IDailyTimeTypes {
  [DayOfWeek.Monday]: IFlattenedTimeType[];
  [DayOfWeek.Tuesday]: IFlattenedTimeType[];
  [DayOfWeek.Wednesday]: IFlattenedTimeType[];
  [DayOfWeek.Thursday]: IFlattenedTimeType[];
  [DayOfWeek.Friday]: IFlattenedTimeType[];
  [DayOfWeek.Saturday]: IFlattenedTimeType[];
  [DayOfWeek.Sunday]: IFlattenedTimeType[];
}

export interface IOfficeGymConfig {
  availabilityLink?: string;
}

export type SupportedIntegrations = 'ClassPass' | 'Textmagic' | 'MessageBird';

export type OnDemandEnabled = 'Never' | 'OffPeakOnly' | 'Always';

export class Gym {
  public static isIntegrationEnabled(gym: Gym, integration: SupportedIntegrations): boolean {
    return !!gym.integrationsEnabled
      && gym.integrationsEnabled.find((added: SupportedIntegrations) => added === integration) !== undefined;
  }

  public static allowsOfficeScheduleStudioBookings(gym: Gym) {
    return gym.type === GymTypes.Home && gym.officeGymConfig !== undefined;
  }

  public name: string;

  public displayName: string;

  public personalisedHeader?: string;

  public lastUpdated?: Date;

  public created: Date;

  public type: GymTypes;

  public numOfZones: number;

  public timezone: GymTimeZone;

  public iso2Country: string;

  public zoneMapping: number[][];

  public publicHolidays: string[];

  public environment: GymEnvironment;

  public allowedOnProductIds: string[] = [];

  public autoCheckin = false;

  public enableStudioAppCheckIn = false;

  public enableStudioWeightRecommendations?: boolean;

  public allowedCapacityAsPercentOfFullGym = 1; // used to reduce the amount of people that can book for a class

  public enabled = true;

  public accountId: string;

  public websiteSlug = '';

  public dummyPriceIdForSignup?: string;

  public displayAddress?: string;

  public email?: string;

  public integrationsEnabled?: SupportedIntegrations[];

  public enableWebDemoBooking?: boolean;

  public defaultHomeSignupFlow?: boolean = false;

  public singleStationPerZoneGym = false;

  public shouldUseInAppPurchaseForMembership = false;

  /**
   * When creating a pre account users will be subscribed to this plan.
   * Because user are automatically subscribed to this plan there is no opportunity to get payment details
   * Therefore either the plan needs to be for 0$ or the coupon needs to be for 100%
   *
   * @see allowUpdatingSubscriptionInSelfManagementPortal
   * @remark If users are allowed update their own subscription the coupon provided here must only apply to the planId
   */
  public subscribeNewUsersToPlanAfterFirstOnboarding?: {
    planId: string;
    coupon?: string;
  };

  public organisation: Organisation;

  public landingPage?: string;

  public bagContactAllowed?: boolean;

  // this field gets set from the gym partial db configs when loading gyms in DbGymRepository
  public enableOnDemandStudioBookings: OnDemandEnabled = 'Never';

  public usersWillExerciseInNextZoneAfterPause? = false;

  /**
   * Subscription updates to be enabled in https://dashboard.stripe.com/test/settings/billing/portal
   *
   * @remark If this setting is toggled on when a timeslot is not available because
   * `TimeSlotEligibility.NotAllowedByPaymentPlan` the user will be prompted to upgrade
   * to enable the slot in the billing portal
   *
   * @see subscribeNewUsersToPlanAfterFirstOnboarding
   * @remark if this field (allowUpdatingSubscriptionInSelfManagementPortal) is set to true make sure that
   * the subscribeNewUsersToPlanAfterFirstOnboarding field does NOT included a coupon or the coupon only applies to the
   * signup / trial product.
   */
  public allowUpdatingSubscriptionInSelfManagementPortal? = false;

  public displayInstructorContentForTimeSlots: TimeSlotType[] = [];

  // this field gets set from the gym partial db configs when loading gyms in DbGymRepository
  public officeGymConfig?: IOfficeGymConfig;

  // This value will be overridden if there will be a pt available in this slot
  public instructorLessTimeSlotDescription: Partial<{ [key in TimeSlotType]: string }> = {};

  // this field gets set from the gym partial db configs when loading gyms in DbGymRepository
  public integrationConfig?: {
    payfast?: IPayfastIntegrationConfig;
  };

  // this field gets set from the gym partial db configs when loading gyms in DbGymRepository
  public companies?: ICompany[];

  constructor(initData?: Partial<Gym>) {
    if (initData) {
      Object.assign(this, initData); // TODO: Object.assign with Partial may result in undefined items
      this.iso2Country = timeZoneISO2CountryMap[this.timezone];
    }
  }
}
